import { translate, type TranslationKey } from "@/utils/i18n";

export type SupportedPhoneCountryCode = "1" | "82";

export type SupportedPhoneCountry = {
  iso: string;
  countryCode: SupportedPhoneCountryCode;
  dialCode: string;
  labelKey: TranslationKey;
  example: string;
};

export const DEFAULT_PHONE_COUNTRY_CODE: SupportedPhoneCountryCode = "82";

export const SUPPORTED_PHONE_COUNTRIES: SupportedPhoneCountry[] = [
  { iso: "US", countryCode: "1", dialCode: "+1", labelKey: "phone.country.us", example: "201-555-0123" },
  { iso: "KR", countryCode: "82", dialCode: "+82", labelKey: "phone.country.kr", example: "010-1234-5678" },
];

export function getPhoneCountry(countryCode: string | null | undefined): SupportedPhoneCountry {
  const normalized = String(countryCode ?? "").replace(/^\+/, "").toLowerCase();
  return (
    SUPPORTED_PHONE_COUNTRIES.find(
      (country) =>
        country.countryCode === normalized ||
        country.iso.toLowerCase() === normalized ||
        country.dialCode.replace("+", "") === normalized,
    ) ?? SUPPORTED_PHONE_COUNTRIES.find((country) => country.countryCode === DEFAULT_PHONE_COUNTRY_CODE)!
  );
}

export function countryFlagEmoji(iso: string): string {
  const normalizedIso = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalizedIso)) return normalizedIso;
  return String.fromCodePoint(
    ...[...normalizedIso].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65),
  );
}

export function formatPhoneForDisplay(
  value: string,
  countryCode: string | null | undefined,
): string {
  const country = getPhoneCountry(countryCode);
  const national = nationalDigitsFromPhone(value, country);

  switch (country.countryCode) {
    case "82":
      return formatKoreanPhone(national);
    case "1":
      return formatChunkedPhone(national, [3, 3, 4]);
    default:
      return national;
  }
}

export function formatPhoneWithDialCodeForDisplay(
  value: string | null | undefined,
  countryCode: string | null | undefined,
): string {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return "";

  const country = phoneCountryFromValue(rawValue, countryCode);
  const national = formatPhoneForDisplay(rawValue, country.countryCode);
  return national ? `${country.dialCode} ${national}` : "";
}

export function formatPhoneForServer(
  value: string,
  countryCode: string | null | undefined,
): string {
  const country = getPhoneCountry(countryCode);
  const national = nationalDigitsFromPhone(value, country);
  const withoutTrunkPrefix = national.replace(/^0+/, "");
  return withoutTrunkPrefix ? `${country.dialCode}${withoutTrunkPrefix}` : "";
}

export function validatePhoneNumber(
  value: string,
  countryCode: string | null | undefined,
): string | undefined {
  const country = getPhoneCountry(countryCode);
  const national = nationalDigitsFromPhone(value, country);

  if (!national) return translate("phone.validation.required");

  const isValid = isValidNationalPhone(national, country);
  if (!isValid) return translate("phone.validation.format", { example: country.example });

  return undefined;
}

function phoneCountryFromValue(
  value: string,
  countryCode: string | null | undefined,
): SupportedPhoneCountry {
  if (countryCode) return getPhoneCountry(countryCode);

  return (
    SUPPORTED_PHONE_COUNTRIES.find((country) => value.trim().startsWith(country.dialCode)) ??
    getPhoneCountry(DEFAULT_PHONE_COUNTRY_CODE)
  );
}

function nationalDigitsFromPhone(value: string, country: SupportedPhoneCountry): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";

  const countryCode = country.countryCode;
  const startsWithDialCode = trimmed.startsWith("+") || digits.length > maxNationalLength(country);
  if (startsWithDialCode && digits.startsWith(countryCode)) {
    return digits.slice(countryCode.length);
  }

  return digits;
}

function isValidNationalPhone(national: string, country: SupportedPhoneCountry): boolean {
  switch (country.countryCode) {
    case "82":
      return /^(010|011|016|017|018|019)\d{7,8}$/.test(ensureLeadingZero(national));
    case "1":
      return /^[2-9]\d{9}$/.test(national);
    default:
      return national.length >= 8;
  }
}

function maxNationalLength(country: SupportedPhoneCountry): number {
  switch (country.countryCode) {
    case "82":
      return 11;
    case "1":
      return 10;
    default:
      return 15;
  }
}

function ensureLeadingZero(value: string): string {
  if (!value) return "";
  return value.startsWith("0") ? value : `0${value}`;
}

function formatKoreanPhone(value: string): string {
  const national = ensureLeadingZero(value);
  if (national.length <= 3) return national;
  if (national.length <= 7) return `${national.slice(0, 3)}-${national.slice(3)}`;
  if (national.length === 10) {
    return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return `${national.slice(0, 3)}-${national.slice(3, 7)}-${national.slice(7, 11)}`;
}

function formatChunkedPhone(value: string, groups: number[]): string {
  const limited = value.slice(0, groups.reduce((sum, group) => sum + group, 0));
  const chunks: string[] = [];
  let cursor = 0;

  for (const group of groups) {
    const chunk = limited.slice(cursor, cursor + group);
    if (!chunk) break;
    chunks.push(chunk);
    cursor += group;
  }

  return chunks.join("-");
}
